import { memo, useCallback, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  useParametricSelector,
  useProfileSelector,
} from "../../../hooks/useParametricSelector";
import { getLoadedUserByUsername } from "../../../redux/slices/dataSlice";
import { useSelector, useDispatch } from "react-redux";
import LoadingScreen from "../../LoadingScreen";
import { getAccountState } from "../../../redux/slices/accountSlice";
import MarkitSecuredContainer from "../../../components/UserInfoContainers/MarkitSecuredContainer";
import { useTheme } from "../../../hooks/useTheme";
import {
  Follower,
  FollowType,
  FormAnswersV2,
  MembershipPlan,
  MembershipState,
  NotificationType,
  SavedFormQuestion,
  VerificationState,
} from "@markit/common.types";
import {
  checkIfFollowerIsSubscribed,
  checkIfLoggedInPhoneNumber,
  checkIfUserIsUnsubscribed,
  checkIfUserIsUnsubscribedMarkit,
  fetchFollowerDataFromPhoneNumber,
} from "../../../utils/userUtils";
import {
  finalizeFormFileUploadAnswers,
  getProfileFormQuestions,
  getUsersFormResponses,
  validateEventForm,
} from "../../../utils/eventUtils/formUtils";
import { createEmptyFormAnswersV2 } from "@markit/common.utils";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useNavigate } from "react-router-dom";
import {
  onProfilePageNavigatePath,
  scrollToTop,
} from "../../../utils/navigationUtils";
import SubscribeFlowInitial from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowInitial";
import SubscribeFlowQuestions from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowQuestions";
import SubscribeFlowMembership from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowMembership";
import SubscribeFlowMembershipCheckout from "../../../components/ProfilePage/SubscribeFlow/SubscribeFlowMembershipCheckout";
import { followUser } from "../../../utils/FollowUser";
import AlertContainer from "../../../components/Containers/AlertContainer";
import { useLoadUserByUsername } from "../../../hooks/useLoadUserByUsername";
import { ContainerButton } from "../../../components/Buttons/ContainerButton";
import { getUserFollowerData } from "../../../utils/FirebaseUtils";
import { API } from "../../../API";
import { FormResponsesProvider } from "../../../components/Form/FormResponsesContext";

export enum MembershipStage {
  MEMBERSHIP = "Membership",
  SUBSCRIBE = "Subscribe",
  QUESTIONS = "Questions",
  MEMBERSHIP_CHECKOUT = "Membership Checkout",
}

const MembershipPage = memo(function MembershipPageFn() {
  const { accountData, loggedIn } = useSelector(getAccountState).account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tempFullName, setTempFullName] = useState("");
  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [verificationState, setVerificationState] = useState(
    VerificationState.UNVERIFIED
  );
  const [uid, setUid] = useState("");
  const [answers, setAnswers] = useState<FormAnswersV2>(
    createEmptyFormAnswersV2([], [])
  );
  const [formErrorIds, setFormErrorIds] = useState<string[]>([]);
  const [formFileUploads, setFormFileUploads] = useState<
    Record<string, string>
  >({});
  const [profileFormQuestions, setProfileFormQuestions] = useState<
    SavedFormQuestion[]
  >([]);
  const [selectedMembershipPlan, setSelectedMembershipPlan] =
    useState<MembershipPlan>();
  const [followerData, setFollowerData] = useState<Follower>();
  const [processing, setProcessing] = useState(false);
  const [optedOut, setOptedOut] = useState(false);
  const [optedOutCreator, setOptedOutCreator] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [alertText, setAlertText] = useState({
    header: "",
    subHeader: "",
  });
  const [membershipStage, setMembershipStage] = useState<MembershipStage>(
    MembershipStage.MEMBERSHIP
  );

  const username = useProfileSelector();
  useLoadUserByUsername(username);

  const {
    isLoading: isLoadingUserData,
    data: creatorData,
    isError: isErrorUserData,
  } = useParametricSelector(getLoadedUserByUsername, username);

  const themeType = useMemo(
    () => creatorData?.membership.theme,
    [creatorData?.membership.theme]
  );
  const { theme } = useTheme(themeType);

  // show all non-hidden membership plans
  const visibleMembershipPlans = useMemo(
    () =>
      creatorData
        ? creatorData.membership.membershipPlans.filter((plan) => !plan.hidden)
        : [],
    [creatorData]
  );

  const membershipsEnabled = useMemo(
    () =>
      creatorData && creatorData?.membership.state === MembershipState.ACTIVE,
    [creatorData]
  );

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (_deps, isLatest) => {
      if (creatorData && initialLoading && isLatest()) {
        // Redirect to profile page if creator does not have memberships enabled
        if (!membershipsEnabled) {
          onProfilePageNavigatePath(navigate, creatorData.username);
          return;
        }

        // fetch the event form questions
        const formQuestions = await getProfileFormQuestions(creatorData.uid);
        setProfileFormQuestions(formQuestions);

        // Default the membership plan to the first paid option, if applicable
        const paidMembershipPlans = visibleMembershipPlans.filter(
          (membership) => membership.price !== 0
        );
        if (paidMembershipPlans.length > 0) {
          setSelectedMembershipPlan(paidMembershipPlans[0]);
        } else {
          setSelectedMembershipPlan(visibleMembershipPlans[0]);
        }

        if (checkIfLoggedInPhoneNumber(loggedIn, accountData.phoneNumber)) {
          setTempFullName(accountData.fullName);
          setTempPhoneNumber(accountData.phoneNumber);
          setVerificationState(VerificationState.VERIFIED);
          setUid(accountData.uid);

          // Check if user is already subscribed
          const followerData = await getUserFollowerData(
            creatorData.uid,
            accountData.uid
          );
          if (
            followerData &&
            checkIfFollowerIsSubscribed(followerData.status)
          ) {
            setFollowerData(followerData);
          } else {
            const [
              isUnsubscribedMarkit,
              isUnsubscribed,
              { formResponses, fileNameRecords },
            ] = await Promise.all([
              checkIfUserIsUnsubscribedMarkit(accountData.phoneNumber),
              checkIfUserIsUnsubscribed(accountData.uid, creatorData.uid),
              getUsersFormResponses(
                creatorData.uid,
                formQuestions,
                accountData.uid
              ),
            ]);
            // Check if logged in phone number is unsubscribed from markit number or creator
            // Even if using firebase OTP, we need them opted in so they can get confirmation texts
            setOptedOut(isUnsubscribedMarkit);
            setOptedOutCreator(isUnsubscribed);
            // set already answered form questions
            setAnswers(createEmptyFormAnswersV2(formQuestions, formResponses));
            setFormFileUploads(fileNameRecords);
          }
        }

        setInitialLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creatorData?.uid, loggedIn]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    // Autofill any form inputs if user is not logged in and has verified their number
    // The logged in case is handled by the useAsyncOnMount above
    if (
      !initialLoading &&
      verificationState === VerificationState.VERIFIED &&
      creatorData
    ) {
      // set already answered form questions
      const { formResponses, fileNameRecords } = await getUsersFormResponses(
        creatorData.uid,
        profileFormQuestions,
        uid
      );
      setAnswers(createEmptyFormAnswersV2(profileFormQuestions, formResponses));
      setFormFileUploads(fileNameRecords);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationState]);

  /**
   * This is only called when the user chooses the free plan. For paid plans, the follow logic is handled in the backend after paying.
   */
  const followUserAccount = useCallback(
    async (answersToSubmit: FormAnswersV2) => {
      if (!creatorData) {
        return;
      }

      await followUser({
        userPhone: tempPhoneNumber,
        userFullName: tempFullName,
        userToFollow: creatorData,
        uid: "",
        answers: answersToSubmit,
        followType: FollowType.PROFILE,
      });

      setProcessing(false);
      onProfilePageNavigatePath(navigate, creatorData.username, "manage");
      showNotificationBanner(
        dispatch,
        "Subscribed",
        NotificationType.AFFIRMATIVE
      );
    },
    [creatorData, dispatch, navigate, tempFullName, tempPhoneNumber]
  );

  /**
   * When the user verifies their number and presses the Subscribe button, here are the cases:
   *  a. Not following, has no questions, chose free plan: subscribe the user
   *  b. Not following, has no questions, chose paid plan: go to membership checkout
   *  c. Not following, has questions: go to questions
   *  d. Following, chose free plan, has free plan: show already subscribed and direct to manage
   *  e. Following, chose paid plan, has free plan: go to membership checkout for upgrade
   *  f. Following, chose paid plan, has paid plan: show already has paid plan and direct to manage
   */
  const verifyNumberOnPress = useCallback(async () => {
    if (!creatorData) {
      return;
    }
    setProcessing(true);
    const follower = await fetchFollowerDataFromPhoneNumber(
      creatorData.uid,
      tempPhoneNumber
    );
    if (follower && checkIfFollowerIsSubscribed(follower.status)) {
      if (follower.membershipPlanId === "free") {
        showNotificationBanner(
          dispatch,
          "Already Subscribed!",
          NotificationType.AFFIRMATIVE
        );
        if (selectedMembershipPlan?.id === "free") {
          onProfilePageNavigatePath(
            navigate,
            creatorData.username,
            membershipsEnabled ? "manage" : undefined,
            true
          );
        } else {
          setMembershipStage(MembershipStage.MEMBERSHIP_CHECKOUT);
        }
      } else {
        onProfilePageNavigatePath(
          navigate,
          creatorData.username,
          membershipsEnabled ? "manage" : undefined,
          true
        );
        showNotificationBanner(
          dispatch,
          "Already on Paid Plan",
          NotificationType.AFFIRMATIVE
        );
      }
    } else {
      if (profileFormQuestions.length > 0) {
        setMembershipStage(MembershipStage.QUESTIONS);
        scrollToTop();
      } else {
        if (selectedMembershipPlan?.id === "free") {
          await followUserAccount(answers);
        } else {
          scrollToTop();
          setMembershipStage(MembershipStage.MEMBERSHIP_CHECKOUT);
        }
      }
    }
    setProcessing(false);
  }, [
    answers,
    creatorData,
    dispatch,
    followUserAccount,
    membershipsEnabled,
    navigate,
    profileFormQuestions.length,
    selectedMembershipPlan?.id,
    tempPhoneNumber,
  ]);

  const saveCheckoutFormResponses = useCallback(async () => {
    if (!creatorData) {
      return;
    }
    const currentAnswers = await finalizeFormFileUploadAnswers({
      creatorUid: creatorData.uid,
      answers: answers,
      formQuestions: profileFormQuestions,
      fileNamesByQuestionId: formFileUploads,
      eventId: "",
      followerUid: uid,
    });

    await API.rsvp.saveUserFormResponse({
      uid: uid,
      creatorUserId: creatorData.uid,
      answers: currentAnswers,
    });
  }, [answers, creatorData, formFileUploads, profileFormQuestions, uid]);

  /**
   * When user submits the questions here are the cases (all not following cases):
   * a. Chose free plan: subscribe the user
   * b. Chose paid plan: go to membership checkout
   */
  const submitQuestionsOnPress = useCallback(async () => {
    if (!creatorData) {
      return;
    }
    setProcessing(true);

    const errorIds = validateEventForm(answers, profileFormQuestions);
    if (errorIds.length > 0) {
      setFormErrorIds(errorIds);
      setAlertText({
        header: "Please answer the required questions.",
        subHeader: "",
      });
      setProcessing(false);
      return;
    }

    let currentAnswers = answers;
    try {
      currentAnswers = await finalizeFormFileUploadAnswers({
        creatorUid: creatorData.uid,
        answers: currentAnswers,
        formQuestions: profileFormQuestions,
        fileNamesByQuestionId: formFileUploads,
        setAnswers,
        eventId: "",
        followerUid: uid,
      });
    } catch (e: any) {
      setProcessing(false);
      alert("Failed to upload a file. Please try again.");
      return;
    }

    if (selectedMembershipPlan?.id === "free") {
      await followUserAccount(currentAnswers);
    } else {
      scrollToTop();
      setMembershipStage(MembershipStage.MEMBERSHIP_CHECKOUT);
    }
    setProcessing(false);
  }, [
    answers,
    creatorData,
    formFileUploads,
    followUserAccount,
    profileFormQuestions,
    selectedMembershipPlan?.id,
    uid,
  ]);

  const chooseMembershipOnPress = useCallback(
    async (selectedMembership: MembershipPlan) => {
      if (!creatorData) {
        return;
      }
      setSelectedMembershipPlan(selectedMembership);
      setMembershipStage(
        followerData
          ? MembershipStage.MEMBERSHIP_CHECKOUT
          : MembershipStage.SUBSCRIBE
      );
    },
    [creatorData, followerData]
  );

  if (isLoadingUserData || initialLoading || !creatorData) {
    return (
      <LoadingScreen
        isLoadingPage={isLoadingUserData || initialLoading || !creatorData}
        error={isErrorUserData}
      />
    );
  }

  const title = `${creatorData.fullName} (@${creatorData.username}) on Markit`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="og:title" content={title} />
        <meta name="og:description" content={creatorData.username} />
      </Helmet>
      <div
        className="ColumnNormalSpaced"
        style={{
          ...theme.SubComponentBG,
          minHeight: "100vh",
          position: "relative",
        }}
      >
        {membershipStage !== MembershipStage.MEMBERSHIP ? (
          <div style={{ position: "absolute", left: 20, top: 20 }}>
            <ContainerButton
              onPress={() =>
                followerData
                  ? setMembershipStage(MembershipStage.MEMBERSHIP)
                  : setMembershipStage(
                      membershipStage === MembershipStage.SUBSCRIBE
                        ? MembershipStage.MEMBERSHIP
                        : membershipStage === MembershipStage.QUESTIONS
                          ? MembershipStage.SUBSCRIBE
                          : MembershipStage.QUESTIONS
                    )
              }
              theme={theme}
            />
          </div>
        ) : null}
        <div style={{ paddingTop: 70 }}>
          {membershipStage === MembershipStage.MEMBERSHIP ? (
            <SubscribeFlowMembership
              creatorData={creatorData}
              chooseOnPress={chooseMembershipOnPress}
              selectedMembershipPlan={selectedMembershipPlan}
              setSelectedMembershipPlan={setSelectedMembershipPlan}
              visibleMembershipPlans={visibleMembershipPlans}
              followerData={followerData}
              themeType={themeType}
            />
          ) : membershipStage === MembershipStage.SUBSCRIBE ? (
            <SubscribeFlowInitial
              creatorData={creatorData}
              processing={processing}
              tempFullName={tempFullName}
              setTempFullName={setTempFullName}
              tempPhoneNumber={tempPhoneNumber}
              setTempPhoneNumber={setTempPhoneNumber}
              verificationState={verificationState}
              setVerificationState={setVerificationState}
              optedOut={optedOut}
              setOptedOut={setOptedOut}
              optedOutCreator={optedOutCreator}
              setOptedOutCreator={setOptedOutCreator}
              verifyOnPress={verifyNumberOnPress}
              setUid={setUid}
              themeType={themeType}
            />
          ) : membershipStage === MembershipStage.QUESTIONS ? (
            <FormResponsesProvider
              formFileUploads={formFileUploads}
              setFormFileUploads={setFormFileUploads}
              formErrorIds={formErrorIds}
              setFormErrorIds={setFormErrorIds}
            >
              <SubscribeFlowQuestions
                creatorData={creatorData}
                profileQuestions={profileFormQuestions}
                processing={processing}
                answers={answers}
                setAnswers={setAnswers}
                verifyOnPress={submitQuestionsOnPress}
                themeType={themeType}
              />
            </FormResponsesProvider>
          ) : membershipStage === MembershipStage.MEMBERSHIP_CHECKOUT &&
            selectedMembershipPlan ? (
            <SubscribeFlowMembershipCheckout
              creatorData={creatorData}
              selectedMembershipPlan={selectedMembershipPlan}
              uid={uid}
              backOnPress={() => setMembershipStage(MembershipStage.MEMBERSHIP)}
              onSaveFormResponses={saveCheckoutFormResponses}
              themeType={themeType}
            />
          ) : null}
        </div>
        <div
          className="Centering"
          style={{ paddingTop: 100, paddingBottom: 30 }}
        >
          <MarkitSecuredContainer />
        </div>
      </div>
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
        theme={theme}
      />
    </>
  );
});

export default MembershipPage;
